import React, { useEffect, useState } from "react";
import { Modal, Tag, Button, Space, Empty, message, Timeline } from "antd";
import { EyeOutlined, FileTextOutlined, ReloadOutlined } from "@ant-design/icons";
import AppTable from "../../common/AppTable";

import {
  getAllShipmentsShippingApi,
  getTrackingShippingApi,
  getLabelShippingApi,
} from "../../services/api";

// Real courier-integrated shipments (Shiprocket etc., via the standalone
// shipping-service microservice) — the single "Shipping" sidebar entry, replacing
// the old manual free-text courier/tracking-number entry page.
const STATUS_COLOR = {
  CREATED: "default",
  AWB_GENERATED: "cyan",
  PICKUP_SCHEDULED: "geekblue",
  PICKED_UP: "blue",
  IN_TRANSIT: "blue",
  OUT_FOR_DELIVERY: "purple",
  DELIVERED: "green",
  RTO_INITIATED: "orange",
  RTO_DELIVERED: "orange",
  CANCELLED: "red",
  FAILED: "red",
};

export default function Shipping() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingEvents, setTrackingEvents] = useState([]);
  const [activeShipment, setActiveShipment] = useState(null);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const res = await getAllShipmentsShippingApi();
      setShipments((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch (e) {
      message.error(e.response?.data?.error?.message || "Failed to load shipments — is shipping-service running?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
  }, []);

  const openTracking = async (shipment) => {
    setActiveShipment(shipment);
    setTrackingOpen(true);
    setTrackingLoading(true);
    try {
      const res = await getTrackingShippingApi(shipment.id);
      setTrackingEvents(res.data.data || []);
    } catch (e) {
      message.error("Failed to load tracking history");
      setTrackingEvents([]);
    } finally {
      setTrackingLoading(false);
    }
  };

  const openLabel = async (shipment) => {
    try {
      const res = await getLabelShippingApi(shipment.id);
      const url = res.data.data?.labelUrl;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        message.warning("No label URL returned yet");
      }
    } catch (e) {
      message.error(e.response?.data?.error?.message || "Failed to generate/fetch label");
    }
  };

  const columns = [
    {
      title: "Order",
      dataIndex: "orderNumber",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, fontFamily: "monospace", fontSize: 13 }}>{v || `#${r.orderId}`}</div>
          <div style={{ fontSize: 11, opacity: 0.55 }}>Vendor #{r.vendorId}</div>
        </div>
      ),
    },
    {
      title: "Courier",
      dataIndex: "courierCode",
      render: (v) => v || "—",
    },
    {
      title: "AWB",
      dataIndex: "awbNumber",
      render: (v) => v || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (
        <Tag color={STATUS_COLOR[v] || "default"} style={{ borderRadius: 12, fontWeight: 600 }}>
          {v || "CREATED"}
        </Tag>
      ),
    },
    {
      title: "Payment",
      dataIndex: "paymentMethod",
      render: (v, r) => (v === "COD" ? `COD · ₹${r.codAmount ?? 0}` : "Prepaid"),
    },
    {
      title: "Freight",
      dataIndex: "freightCharge",
      render: (v) => (v != null ? `₹${v}` : "—"),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "Actions",
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openTracking(r)}>
            Tracking
          </Button>
          <Button size="small" icon={<FileTextOutlined />} onClick={() => openLabel(r)}>
            Label
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Shipping</div>
          <div className="page-sub">{shipments.length} courier shipment(s)</div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchShipments} loading={loading}>
          Refresh
        </Button>
      </div>

      <AppTable columns={columns} data={shipments} loading={loading} />

      <Modal
        title={activeShipment ? `Tracking — ${activeShipment.orderNumber || "#" + activeShipment.orderId}` : "Tracking"}
        open={trackingOpen}
        onCancel={() => setTrackingOpen(false)}
        footer={null}
      >
        {trackingLoading ? (
          <div style={{ textAlign: "center", padding: 24 }}>Loading…</div>
        ) : trackingEvents.length === 0 ? (
          <Empty description="No tracking events yet" />
        ) : (
          <Timeline
            items={trackingEvents.map((e) => ({
              color: STATUS_COLOR[e.normalizedStatus] || "gray",
              children: (
                <div>
                  <div style={{ fontWeight: 600 }}>{e.normalizedStatus}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{e.description || e.rawStatus}</div>
                  <div style={{ fontSize: 11, opacity: 0.5 }}>
                    {e.eventTime ? new Date(e.eventTime).toLocaleString() : ""} {e.location ? `· ${e.location}` : ""} · {e.source}
                  </div>
                </div>
              ),
            }))}
          />
        )}
      </Modal>
    </div>
  );
}
